import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

/**
 * PhoneInput Component for Syrian Phone Numbers
 * Format: +963 931-443-885
 * API Format: +963931443885
 */
const PhoneInput = ({
  label,
  name,
  placeholder = "9xx-xxx-xxxx",
  value = "",
  handleChange,
  required = false,
  disabled = false,
  error = "",
  className = "",
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [localDisplayValue, setLocalDisplayValue] = useState("");
  const inputRef = useRef(null);
  const isUserEditing = useRef(false); // Track if user is actively editing
  const COUNTRY_CODE = "+963";
  
  // Format phone number for display: +963 931-443-885
  const formatPhoneNumber = React.useCallback((phone) => {
    if (!phone) return "";
    
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, "");
    
    // Remove country code if present
    if (cleaned.startsWith("+963")) {
      cleaned = cleaned.replace("+963", "");
    } else if (cleaned.startsWith("963")) {
      cleaned = cleaned.replace("963", "");
    }
    
    // Remove leading 0 if present
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.substring(1);
    }
    
    // Only keep digits, max 9
    cleaned = cleaned.replace(/\D/g, "").substring(0, 9);
    
    // Format: XXX-XXX-XXX
    if (cleaned.length > 6) {
      return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
    } else if (cleaned.length > 3) {
      return `${cleaned.substring(0, 3)}-${cleaned.substring(3)}`;
    }
    
    return cleaned;
  }, []);
  
  // Parse phone from API format: +963931443885 -> 931-443-885
  const parsePhoneFromAPI = React.useCallback((apiPhone) => {
    if (!apiPhone) return "";
    
    // Handle string values
    let phoneStr = typeof apiPhone === 'string' ? apiPhone : String(apiPhone);
    
    // Remove country code
    let cleaned = phoneStr.replace(/^\+?963/, "");
    
    // Remove leading 0 if present
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.substring(1);
    }
    
    // Format for display
    return formatPhoneNumber(cleaned);
  }, [formatPhoneNumber]);
  
  // Sync local display value with prop value when it changes from outside
  // Only sync when NOT actively editing (to prevent clearing during typing)
  useEffect(() => {
    if (!isUserEditing.current) {
      if (value) {
        const parsed = parsePhoneFromAPI(value);
        setLocalDisplayValue(parsed);
      } else {
        setLocalDisplayValue("");
      }
    }
  }, [value, parsePhoneFromAPI]);
  
  // Clean phone number for API: +963931443885
  const cleanPhoneNumber = (formattedPhone) => {
    if (!formattedPhone) return "";
    
    // Remove all non-digit characters
    let cleaned = formattedPhone.replace(/\D/g, "");
    
    // Remove country code if present
    if (cleaned.startsWith("963")) {
      cleaned = cleaned.replace("963", "");
    }
    
    // Remove leading 0 if present
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.substring(1);
    }
    
    // Ensure exactly 9 digits
    cleaned = cleaned.substring(0, 9);
    
    // Return with country code
    return cleaned.length === 9 ? `${COUNTRY_CODE}${cleaned}` : "";
  };
  
  // Use local display value for input, fallback to parsed value prop
  const displayValue = localDisplayValue || (value ? parsePhoneFromAPI(value) : "");
  const hasValue = displayValue.length > 0;
  const showLabelFloat = isFocused || hasValue;
  const showPlaceholder = !hasValue && placeholder;
  
  // Handle input change
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // Mark that user is actively editing
    isUserEditing.current = true;
    
    // Remove all non-digit characters (user only types numbers)
    let digitsOnly = inputValue.replace(/\D/g, "");
    
    // Remove leading 0 if user tries to enter it
    if (digitsOnly.startsWith("0")) {
      digitsOnly = digitsOnly.substring(1);
    }
    
    // Limit to 9 digits
    digitsOnly = digitsOnly.substring(0, 9);
    
    // Format the display value and update local state
    const formatted = formatPhoneNumber(digitsOnly);
    setLocalDisplayValue(formatted);
    
    // Clean for API (with country code)
    // Only set value if we have 9 digits, otherwise set empty string
    const cleaned = digitsOnly.length === 9 ? cleanPhoneNumber(digitsOnly) : "";
    
    // Create synthetic event with cleaned value for API
    handleChange({
      target: {
        name: name,
        value: cleaned, // Store cleaned value (+963931443885) or empty string
      },
    });
  };
  
  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData("text");
    
    // Mark that user is actively editing
    isUserEditing.current = true;
    
    // Remove all non-digit characters
    let digitsOnly = pastedText.replace(/\D/g, "");
    
    // Handle country code
    if (digitsOnly.startsWith("963")) {
      digitsOnly = digitsOnly.replace("963", "");
    }
    
    // Remove leading 0
    if (digitsOnly.startsWith("0")) {
      digitsOnly = digitsOnly.substring(1);
    }
    
    // Limit to 9 digits
    digitsOnly = digitsOnly.substring(0, 9);
    
    // Format and update
    const formatted = formatPhoneNumber(digitsOnly);
    setLocalDisplayValue(formatted);
    
    // Only set value if we have 9 digits
    const cleaned = digitsOnly.length === 9 ? cleanPhoneNumber(digitsOnly) : "";
    
    handleChange({
      target: {
        name: name,
        value: cleaned,
      },
    });
  };
  
  // Handle keydown to prevent invalid characters
  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, and arrow keys
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    
    // Ensure that it is a number and stop the keypress if not
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };
  
  return (
    <div className={`relative ${className}`}>
      {/* Label - Always show, floats when focused or has value */}
      <motion.label
        htmlFor={name}
        className={`absolute left-3 transition-all duration-200 pointer-events-none z-20 ${
          showLabelFloat
            ? "top-2 text-xs text-blue-600 dark:text-blue-400 font-medium"
            : "top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400 opacity-0"
        } ${error ? "text-red-600 dark:text-red-400" : ""}`}
        animate={{
          y: showLabelFloat ? 0 : 0,
          scale: showLabelFloat ? 0.85 : 1,
          opacity: showLabelFloat ? 1 : 0,
        }}
        transition={{ duration: 0.2 }}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </motion.label>
      
      <div className="relative flex items-center">
        {/* Country Code Badge - Static, vertically centered with input */}
        <div className="absolute left-3 z-10 flex items-center top-1/2 -translate-y-1/2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-transparent">
            {COUNTRY_CODE}
          </span>
          <span className="ml-2 text-gray-400 dark:text-gray-500">|</span>
        </div>
        
        {/* Input Field - Keep consistent padding to maintain vertical centering */}
        <motion.input
          ref={inputRef}
          type="text"
          name={name}
          id={name}
          placeholder={showPlaceholder ? placeholder : ""}
          value={displayValue}
          onChange={handleInputChange}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            isUserEditing.current = false; // Reset editing flag on blur
          }}
          required={required}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
          maxLength={13} // 9 digits + 2 dashes = 11, but allow some buffer
          className={`
            w-full pl-16 pr-3
            py-3.5
            text-sm text-gray-900 dark:text-white 
            placeholder:text-gray-400 dark:placeholder:text-gray-500
            placeholder:opacity-100
            bg-white dark:bg-gray-800
            border-2 rounded-lg
            transition-all duration-200
            focus:outline-none focus:ring-0
            disabled:bg-gray-50 dark:disabled:bg-gray-900
            disabled:cursor-not-allowed
            disabled:opacity-60
            ${
              error
                ? "border-red-500 focus:border-red-600 dark:border-red-500 dark:focus:border-red-600"
                : isFocused
                ? "border-blue-500 focus:border-blue-600 dark:border-blue-500 dark:focus:border-blue-600"
                : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
            }
          `}
          whileFocus={{ scale: 1.01 }}
          transition={{ duration: 0.2 }}
        />
        
        {/* Focus indicator line */}
        {isFocused && !error && (
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-b-lg"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            exit={{ scaleX: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
        
        {/* Error indicator line */}
        {error && (
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-500 rounded-b-lg"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <motion.p
          id={`${name}-error`}
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </motion.p>
      )}
    </div>
  );
};

export default PhoneInput;
